import {createReducer, PayloadAction} from '@reduxjs/toolkit';
import {
  EVALUATION_MEETINGS,
  INTERNSHIP_EXPERIENCE,
  ALL_SEASONS_QUESTIONS,
  LEARNING_CONTRACTS,
  ALL_SEASONS_INTERN_EXPERIENCE_TASK,
  MATCHED_STUDENTS,
  SUMMER_TO_SUMMER_RETENTION,
  LOCATION_INTERNS,
  SEASON_INTERNS,
  INTERNS_LIST,
  UPCOMING_INTERN_DEGREES,
  UPCOMING_INTERN_LOCATIONS,
  COMPANY_HISTORICAL_INTERN_DEGREES,
  HISTORICAL_INTERNSHIP_LOCATIONS,
  INTERNSHIP_HISTORY,
  INTERN_DEGREE_POOLS,
} from './Actions';
import {
  ICompanyInternshipHistoryParamsModel,
  IInternshipHistoryRequest,
} from '../../types/requests/CompanyInternshipHistoryParamsModel';
import {CompanyInternshipSeasonDataModel} from '../../types/models/CompanyInternshipSeasonDataModel';
import {
  ICompanyInternDegreePoolsParamsModel,
  IInternDegreePoolsRequest,
} from '../../types/requests/CompanyInternDegreePoolsParamsModel';
import {CompanyInternDegreePoolsDataModel} from '../../types/models/CompanyInternDegreePoolsDataModel';
import {CompanyInternLocationsDataModel} from '../../types/models/CompanyInternLocationsDataModel';
import {CompanySummerRetentionDataModel} from '../../types/models/SummerRetentionDataModel';

export interface ICompanyInsightsState {
  internshipHistorySearchParams: ICompanyInternshipHistoryParamsModel | undefined;
  internshipHistoryResultLoading: boolean;
  internshipHistoryResultRefreshing: boolean;
  internshipHistoryResultError: string | undefined;
  internshipHistoryResult: CompanyInternshipSeasonDataModel[] | undefined;

  internDegreePoolsSearchParams: ICompanyInternDegreePoolsParamsModel | undefined;
  internDegreePoolsResultLoading: boolean;
  internDegreePoolsResultRefreshing: boolean;
  internDegreePoolsResultError: string | undefined;
  internDegreePoolsResult: CompanyInternDegreePoolsDataModel[] | undefined;

  historicalInternshipLocationsResultLoading: boolean;
  historicalInternshipLocationsResultRefreshing: boolean;
  historicalInternshipLocationsResultError: string | undefined;
  historicalInternshipLocationsResult: CompanyInternLocationsDataModel[] | undefined;

  companyHistoricalInternDegreesResultLoading: boolean;
  companyHistoricalInternDegreesResultRefreshing: boolean;
  companyHistoricalInternDegreesResultError: string | undefined;
  companyHistoricalInternDegreesResult: CompanyInternDegreePoolsDataModel[] | undefined;

  matchedStudentsResultLoading: boolean;
  matchedStudentsResultRefreshing: boolean;
  matchedStudentsResultError: string | undefined;
  matchedStudentsResult: CompanyInternshipSeasonDataModel[] | undefined;

  upcomingInternLocationsResultLoading: boolean;
  upcomingInternLocationsResultRefreshing: boolean;
  upcomingInternLocationsResultError: string | undefined;
  upcomingInternLocationsResult: CompanyInternLocationsDataModel[] | undefined;

  upcomingInternDegreesResultLoading: boolean;
  upcomingInternDegreesResultRefreshing: boolean;
  upcomingInternDegreesResultError: string | undefined;
  upcomingInternDegreesResult: CompanyInternDegreePoolsDataModel[] | undefined;

  internsListSearchParams: ICompanyInternshipHistoryParamsModel | undefined;
  internsListResultLoading: boolean;
  internsListResultRefreshing: boolean;
  internsListResultError: string | undefined;
  internsListResult: CompanyInternshipSeasonDataModel[] | undefined;

  seasonInternsResultLoading: boolean;
  seasonInternsResultRefreshing: boolean;
  seasonInternsResultError: string | undefined;
  seasonInternsResult: CompanyInternshipSeasonDataModel[] | undefined;

  locationInternsResultLoading: boolean;
  locationInternsResultRefreshing: boolean;
  locationInternsResultError: string | undefined;
  locationInternsResult: CompanyInternshipSeasonDataModel[] | undefined;

  summerToSummerRetentionSearchParams: ICompanyInternshipHistoryParamsModel | undefined;
  summerToSummerRetentionResultLoading: boolean;
  summerToSummerRetentionResultRefreshing: boolean;
  summerToSummerRetentionResultError: string | undefined;
  summerToSummerRetentionResult: CompanySummerRetentionDataModel | undefined;

  avgSummerInternshipLengthSearchParams: ICompanyInternshipHistoryParamsModel | undefined;
  avgSummerInternshipLengthResultLoading: boolean;
  avgSummerInternshipLengthResultRefreshing: boolean;
  avgSummerInternshipLengthResultError: string | undefined;
  avgSummerInternshipLengthResult: CompanyInternshipSeasonDataModel[] | undefined;

  allSeasonsInternExperienceTaskResultLoading: boolean;
  allSeasonsInternExperienceTaskResultRefreshing: boolean;
  allSeasonsInternExperienceTaskResultError: string | undefined;
  allSeasonsInternExperienceTaskResult: CompanyInternshipSeasonDataModel[] | undefined;

  allSeasonsQuestionsResultLoading: boolean;
  allSeasonsQuestionsResultRefreshing: boolean;
  allSeasonsQuestionsResultError: string | undefined;
  allSeasonsQuestionsResult: CompanyInternshipSeasonDataModel[] | undefined;

  learningContractsSearchParams: ICompanyInternshipHistoryParamsModel | undefined;
  learningContractsResultLoading: boolean;
  learningContractsResultRefreshing: boolean;
  learningContractsResultError: string | undefined;
  learningContractsResult: CompanyInternshipSeasonDataModel[] | undefined;

  internshipExperienceSearchParams: ICompanyInternshipHistoryParamsModel | undefined;
  internshipExperienceResultLoading: boolean;
  internshipExperienceResultRefreshing: boolean;
  internshipExperienceResultError: string | undefined;
  internshipExperienceResult: CompanyInternshipSeasonDataModel[] | undefined;

  evaluationMeetingsSearchParams: ICompanyInternshipHistoryParamsModel | undefined;
  evaluationMeetingsResultLoading: boolean;
  evaluationMeetingsResultRefreshing: boolean;
  evaluationMeetingsResultError: string | undefined;
  evaluationMeetingsResult: CompanyInternshipSeasonDataModel[] | undefined;
}

const initialState: ICompanyInsightsState = {
  internshipHistorySearchParams: undefined,
  internshipHistoryResultLoading: false,
  internshipHistoryResultRefreshing: false,
  internshipHistoryResultError: undefined,
  internshipHistoryResult: undefined,

  internDegreePoolsSearchParams: undefined,
  internDegreePoolsResultLoading: false,
  internDegreePoolsResultRefreshing: false,
  internDegreePoolsResultError: undefined,
  internDegreePoolsResult: undefined,

  historicalInternshipLocationsResultLoading: false,
  historicalInternshipLocationsResultRefreshing: false,
  historicalInternshipLocationsResultError: undefined,
  historicalInternshipLocationsResult: undefined,

  companyHistoricalInternDegreesResultLoading: false,
  companyHistoricalInternDegreesResultRefreshing: false,
  companyHistoricalInternDegreesResultError: undefined,
  companyHistoricalInternDegreesResult: undefined,

  matchedStudentsResultLoading: false,
  matchedStudentsResultRefreshing: false,
  matchedStudentsResultError: undefined,
  matchedStudentsResult: undefined,

  upcomingInternLocationsResultLoading: false,
  upcomingInternLocationsResultRefreshing: false,
  upcomingInternLocationsResultError: undefined,
  upcomingInternLocationsResult: undefined,

  upcomingInternDegreesResultLoading: false,
  upcomingInternDegreesResultRefreshing: false,
  upcomingInternDegreesResultError: undefined,
  upcomingInternDegreesResult: undefined,

  internsListSearchParams: undefined,
  internsListResultLoading: false,
  internsListResultRefreshing: false,
  internsListResultError: undefined,
  internsListResult: undefined,

  seasonInternsResultLoading: false,
  seasonInternsResultRefreshing: false,
  seasonInternsResultError: undefined,
  seasonInternsResult: undefined,

  locationInternsResultLoading: false,
  locationInternsResultRefreshing: false,
  locationInternsResultError: undefined,
  locationInternsResult: undefined,

  summerToSummerRetentionSearchParams: undefined,
  summerToSummerRetentionResultLoading: false,
  summerToSummerRetentionResultRefreshing: false,
  summerToSummerRetentionResultError: undefined,
  summerToSummerRetentionResult: undefined,

  avgSummerInternshipLengthSearchParams: undefined,
  avgSummerInternshipLengthResultLoading: false,
  avgSummerInternshipLengthResultRefreshing: false,
  avgSummerInternshipLengthResultError: undefined,
  avgSummerInternshipLengthResult: undefined,

  allSeasonsInternExperienceTaskResultLoading: false,
  allSeasonsInternExperienceTaskResultRefreshing: false,
  allSeasonsInternExperienceTaskResultError: undefined,
  allSeasonsInternExperienceTaskResult: undefined,

  allSeasonsQuestionsResultLoading: false,
  allSeasonsQuestionsResultRefreshing: false,
  allSeasonsQuestionsResultError: undefined,
  allSeasonsQuestionsResult: undefined,

  learningContractsSearchParams: undefined,
  learningContractsResultLoading: false,
  learningContractsResultRefreshing: false,
  learningContractsResultError: undefined,
  learningContractsResult: undefined,

  internshipExperienceSearchParams: undefined,
  internshipExperienceResultLoading: false,
  internshipExperienceResultRefreshing: false,
  internshipExperienceResultError: undefined,
  internshipExperienceResult: undefined,

  evaluationMeetingsSearchParams: undefined,
  evaluationMeetingsResultLoading: false,
  evaluationMeetingsResultRefreshing: false,
  evaluationMeetingsResultError: undefined,
  evaluationMeetingsResult: undefined,
};

const companyInsightsReducer = createReducer(initialState, {
  [INTERNSHIP_HISTORY.LOAD]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.internshipHistorySearchParams = action.payload.searchParams;
    state.internshipHistoryResultLoading = true;
    state.internshipHistoryResultRefreshing = false;
    state.internshipHistoryResultError = undefined;
  },
  [INTERNSHIP_HISTORY.REFRESH]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.internshipHistorySearchParams = action.payload.searchParams;
    state.internshipHistoryResultLoading = false;
    state.internshipHistoryResultRefreshing = true;
    state.internshipHistoryResultError = undefined;
  },
  [INTERNSHIP_HISTORY.ERROR]: (state, action: PayloadAction<string>) => {
    state.internshipHistoryResultLoading = false;
    state.internshipHistoryResultRefreshing = false;
    state.internshipHistoryResultError = action.payload;
  },
  [INTERNSHIP_HISTORY.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.internshipHistoryResultLoading = false;
    state.internshipHistoryResultRefreshing = false;
    state.internshipHistoryResultError = undefined;
    state.internshipHistoryResult = action.payload;
  },

  [INTERN_DEGREE_POOLS.LOAD]: (state, action: PayloadAction<IInternDegreePoolsRequest>) => {
    state.internDegreePoolsSearchParams = action.payload.searchParams;
    state.internDegreePoolsResultLoading = true;
    state.internDegreePoolsResultRefreshing = false;
    state.internDegreePoolsResultError = undefined;
  },
  [INTERN_DEGREE_POOLS.REFRESH]: (state, action: PayloadAction<IInternDegreePoolsRequest>) => {
    state.internDegreePoolsSearchParams = action.payload.searchParams;
    state.internDegreePoolsResultLoading = false;
    state.internDegreePoolsResultRefreshing = true;
    state.internDegreePoolsResultError = undefined;
  },
  [INTERN_DEGREE_POOLS.ERROR]: (state, action: PayloadAction<string>) => {
    state.internDegreePoolsResultLoading = false;
    state.internDegreePoolsResultRefreshing = false;
    state.internDegreePoolsResultError = action.payload;
  },
  [INTERN_DEGREE_POOLS.LOADED]: (state, action: PayloadAction<CompanyInternDegreePoolsDataModel[]>) => {
    state.internDegreePoolsResultLoading = false;
    state.internDegreePoolsResultRefreshing = false;
    state.internDegreePoolsResultError = undefined;
    state.internDegreePoolsResult = action.payload;
  },

  [HISTORICAL_INTERNSHIP_LOCATIONS.LOAD]: (state, action: PayloadAction<string>) => {
    state.historicalInternshipLocationsResultLoading = true;
    state.historicalInternshipLocationsResultRefreshing = false;
    state.historicalInternshipLocationsResultError = undefined;
  },
  [HISTORICAL_INTERNSHIP_LOCATIONS.REFRESH]: (state, action: PayloadAction<string>) => {
    state.historicalInternshipLocationsResultLoading = false;
    state.historicalInternshipLocationsResultRefreshing = true;
    state.historicalInternshipLocationsResultError = undefined;
  },
  [HISTORICAL_INTERNSHIP_LOCATIONS.ERROR]: (state, action: PayloadAction<string>) => {
    state.historicalInternshipLocationsResultLoading = false;
    state.historicalInternshipLocationsResultRefreshing = false;
    state.historicalInternshipLocationsResultError = action.payload;
  },
  [HISTORICAL_INTERNSHIP_LOCATIONS.LOADED]: (state, action: PayloadAction<CompanyInternLocationsDataModel[]>) => {
    state.historicalInternshipLocationsResultLoading = false;
    state.historicalInternshipLocationsResultRefreshing = false;
    state.historicalInternshipLocationsResultError = undefined;
    state.historicalInternshipLocationsResult = action.payload;
  },

  [COMPANY_HISTORICAL_INTERN_DEGREES.LOAD]: (state, action: PayloadAction<string>) => {
    state.companyHistoricalInternDegreesResultLoading = true;
    state.companyHistoricalInternDegreesResultRefreshing = false;
    state.companyHistoricalInternDegreesResultError = undefined;
  },
  [COMPANY_HISTORICAL_INTERN_DEGREES.REFRESH]: (state, action: PayloadAction<string>) => {
    state.companyHistoricalInternDegreesResultLoading = false;
    state.companyHistoricalInternDegreesResultRefreshing = true;
    state.companyHistoricalInternDegreesResultError = undefined;
  },
  [COMPANY_HISTORICAL_INTERN_DEGREES.ERROR]: (state, action: PayloadAction<string>) => {
    state.companyHistoricalInternDegreesResultLoading = false;
    state.companyHistoricalInternDegreesResultRefreshing = false;
    state.companyHistoricalInternDegreesResultError = action.payload;
  },
  [COMPANY_HISTORICAL_INTERN_DEGREES.LOADED]: (state, action: PayloadAction<CompanyInternDegreePoolsDataModel[]>) => {
    state.companyHistoricalInternDegreesResultLoading = false;
    state.companyHistoricalInternDegreesResultRefreshing = false;
    state.companyHistoricalInternDegreesResultError = undefined;
    state.companyHistoricalInternDegreesResult = action.payload;
  },

  [MATCHED_STUDENTS.LOAD]: (state, action: PayloadAction<string>) => {
    state.matchedStudentsResultLoading = true;
    state.matchedStudentsResultRefreshing = false;
    state.matchedStudentsResultError = undefined;
  },
  [MATCHED_STUDENTS.REFRESH]: (state, action: PayloadAction<string>) => {
    state.matchedStudentsResultLoading = false;
    state.matchedStudentsResultRefreshing = true;
    state.matchedStudentsResultError = undefined;
  },
  [MATCHED_STUDENTS.ERROR]: (state, action: PayloadAction<string>) => {
    state.matchedStudentsResultLoading = false;
    state.matchedStudentsResultRefreshing = false;
    state.matchedStudentsResultError = action.payload;
  },
  [MATCHED_STUDENTS.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.matchedStudentsResultLoading = false;
    state.matchedStudentsResultRefreshing = false;
    state.matchedStudentsResultError = undefined;
    state.matchedStudentsResult = action.payload;
  },

  [UPCOMING_INTERN_LOCATIONS.LOAD]: (state, action: PayloadAction<string>) => {
    state.upcomingInternLocationsResultLoading = true;
    state.upcomingInternLocationsResultRefreshing = false;
    state.upcomingInternLocationsResultError = undefined;
  },
  [UPCOMING_INTERN_LOCATIONS.REFRESH]: (state, action: PayloadAction<string>) => {
    state.upcomingInternLocationsResultLoading = false;
    state.upcomingInternLocationsResultRefreshing = true;
    state.upcomingInternLocationsResultError = undefined;
  },
  [UPCOMING_INTERN_LOCATIONS.ERROR]: (state, action: PayloadAction<string>) => {
    state.upcomingInternLocationsResultLoading = false;
    state.upcomingInternLocationsResultRefreshing = false;
    state.upcomingInternLocationsResultError = action.payload;
  },
  [UPCOMING_INTERN_LOCATIONS.LOADED]: (state, action: PayloadAction<CompanyInternLocationsDataModel[]>) => {
    state.upcomingInternLocationsResultLoading = false;
    state.upcomingInternLocationsResultRefreshing = false;
    state.upcomingInternLocationsResultError = undefined;
    state.upcomingInternLocationsResult = action.payload;
  },

  [UPCOMING_INTERN_DEGREES.LOAD]: (state, action: PayloadAction<string>) => {
    state.upcomingInternDegreesResultLoading = true;
    state.upcomingInternDegreesResultRefreshing = false;
    state.upcomingInternDegreesResultError = undefined;
  },
  [UPCOMING_INTERN_DEGREES.REFRESH]: (state, action: PayloadAction<string>) => {
    state.upcomingInternDegreesResultLoading = false;
    state.upcomingInternDegreesResultRefreshing = true;
    state.upcomingInternDegreesResultError = undefined;
  },
  [UPCOMING_INTERN_DEGREES.ERROR]: (state, action: PayloadAction<string>) => {
    state.upcomingInternDegreesResultLoading = false;
    state.upcomingInternDegreesResultRefreshing = false;
    state.upcomingInternDegreesResultError = action.payload;
  },
  [UPCOMING_INTERN_DEGREES.LOADED]: (state, action: PayloadAction<CompanyInternDegreePoolsDataModel[]>) => {
    state.upcomingInternDegreesResultLoading = false;
    state.upcomingInternDegreesResultRefreshing = false;
    state.upcomingInternDegreesResultError = undefined;
    state.upcomingInternDegreesResult = action.payload;
  },

  [INTERNS_LIST.LOAD]: (state, action: PayloadAction<string>) => {
    state.internsListResultLoading = true;
    state.internsListResultRefreshing = false;
    state.internsListResultError = undefined;
  },
  [INTERNS_LIST.REFRESH]: (state, action: PayloadAction<string>) => {
    state.internsListResultLoading = false;
    state.internsListResultRefreshing = true;
    state.internsListResultError = undefined;
  },
  [INTERNS_LIST.ERROR]: (state, action: PayloadAction<string>) => {
    state.internsListResultLoading = false;
    state.internsListResultRefreshing = false;
    state.internsListResultError = action.payload;
  },
  [INTERNS_LIST.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.internsListResultLoading = false;
    state.internsListResultRefreshing = false;
    state.internsListResultError = undefined;
    state.internsListResult = action.payload;
  },

  [SEASON_INTERNS.LOAD]: (state, action: PayloadAction<string>) => {
    state.seasonInternsResultLoading = true;
    state.seasonInternsResultRefreshing = false;
    state.seasonInternsResultError = undefined;
  },
  [SEASON_INTERNS.REFRESH]: (state, action: PayloadAction<string>) => {
    state.seasonInternsResultLoading = false;
    state.seasonInternsResultRefreshing = true;
    state.seasonInternsResultError = undefined;
  },
  [SEASON_INTERNS.ERROR]: (state, action: PayloadAction<string>) => {
    state.seasonInternsResultLoading = false;
    state.seasonInternsResultRefreshing = false;
    state.seasonInternsResultError = action.payload;
  },
  [SEASON_INTERNS.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.seasonInternsResultLoading = false;
    state.seasonInternsResultRefreshing = false;
    state.seasonInternsResultError = undefined;
    state.seasonInternsResult = action.payload;
  },

  [LOCATION_INTERNS.LOAD]: (state, action: PayloadAction<string>) => {
    state.locationInternsResultLoading = true;
    state.locationInternsResultRefreshing = false;
    state.locationInternsResultError = undefined;
  },
  [LOCATION_INTERNS.REFRESH]: (state, action: PayloadAction<string>) => {
    state.locationInternsResultLoading = false;
    state.locationInternsResultRefreshing = true;
    state.locationInternsResultError = undefined;
  },
  [LOCATION_INTERNS.ERROR]: (state, action: PayloadAction<string>) => {
    state.locationInternsResultLoading = false;
    state.locationInternsResultRefreshing = false;
    state.locationInternsResultError = action.payload;
  },
  [LOCATION_INTERNS.LOADED]: (state, action: PayloadAction<CompanyInternLocationsDataModel[]>) => {
    state.locationInternsResultLoading = false;
    state.locationInternsResultRefreshing = false;
    state.locationInternsResultError = undefined;
    state.locationInternsResult = action.payload;
  },

  [SUMMER_TO_SUMMER_RETENTION.LOAD]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.summerToSummerRetentionSearchParams = action.payload.searchParams;
    state.summerToSummerRetentionResultLoading = true;
    state.summerToSummerRetentionResultRefreshing = false;
    state.summerToSummerRetentionResultError = undefined;
  },
  [SUMMER_TO_SUMMER_RETENTION.REFRESH]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.summerToSummerRetentionSearchParams = action.payload.searchParams;
    state.summerToSummerRetentionResultLoading = false;
    state.summerToSummerRetentionResultRefreshing = true;
    state.summerToSummerRetentionResultError = undefined;
  },
  [SUMMER_TO_SUMMER_RETENTION.ERROR]: (state, action: PayloadAction<string>) => {
    state.summerToSummerRetentionResultLoading = false;
    state.summerToSummerRetentionResultRefreshing = false;
    state.summerToSummerRetentionResultError = action.payload;
  },
  [SUMMER_TO_SUMMER_RETENTION.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.summerToSummerRetentionResultLoading = false;
    state.summerToSummerRetentionResultRefreshing = false;
    state.summerToSummerRetentionResultError = undefined;
    state.summerToSummerRetentionResult = action.payload;
  },

  [ALL_SEASONS_INTERN_EXPERIENCE_TASK.LOAD]: (state, action: PayloadAction<string>) => {
    state.allSeasonsInternExperienceTaskResultLoading = true;
    state.allSeasonsInternExperienceTaskResultRefreshing = false;
    state.allSeasonsInternExperienceTaskResultError = undefined;
  },
  [ALL_SEASONS_INTERN_EXPERIENCE_TASK.REFRESH]: (state, action: PayloadAction<string>) => {
    state.allSeasonsInternExperienceTaskResultLoading = false;
    state.allSeasonsInternExperienceTaskResultRefreshing = true;
    state.allSeasonsInternExperienceTaskResultError = undefined;
  },
  [ALL_SEASONS_INTERN_EXPERIENCE_TASK.ERROR]: (state, action: PayloadAction<string>) => {
    state.allSeasonsInternExperienceTaskResultLoading = false;
    state.allSeasonsInternExperienceTaskResultRefreshing = false;
    state.allSeasonsInternExperienceTaskResultError = action.payload;
  },
  [ALL_SEASONS_INTERN_EXPERIENCE_TASK.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.allSeasonsInternExperienceTaskResultLoading = false;
    state.allSeasonsInternExperienceTaskResultRefreshing = false;
    state.allSeasonsInternExperienceTaskResultError = undefined;
    state.allSeasonsInternExperienceTaskResult = action.payload;
  },

  [ALL_SEASONS_QUESTIONS.LOAD]: (state, action: PayloadAction<string>) => {
    state.allSeasonsQuestionsResultLoading = true;
    state.allSeasonsQuestionsResultRefreshing = false;
    state.allSeasonsQuestionsResultError = undefined;
  },
  [ALL_SEASONS_QUESTIONS.REFRESH]: (state, action: PayloadAction<string>) => {
    state.allSeasonsQuestionsResultLoading = false;
    state.allSeasonsQuestionsResultRefreshing = true;
    state.allSeasonsQuestionsResultError = undefined;
  },
  [ALL_SEASONS_QUESTIONS.ERROR]: (state, action: PayloadAction<string>) => {
    state.allSeasonsQuestionsResultLoading = false;
    state.allSeasonsQuestionsResultRefreshing = false;
    state.allSeasonsQuestionsResultError = action.payload;
  },
  [ALL_SEASONS_QUESTIONS.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.allSeasonsQuestionsResultLoading = false;
    state.allSeasonsQuestionsResultRefreshing = false;
    state.allSeasonsQuestionsResultError = undefined;
    state.allSeasonsQuestionsResult = action.payload;
  },

  [LEARNING_CONTRACTS.LOAD]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.learningContractsSearchParams = action.payload.searchParams;
    state.learningContractsResultLoading = true;
    state.learningContractsResultRefreshing = false;
    state.learningContractsResultError = undefined;
  },
  [LEARNING_CONTRACTS.REFRESH]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.learningContractsSearchParams = action.payload.searchParams;
    state.learningContractsResultLoading = false;
    state.learningContractsResultRefreshing = true;
    state.learningContractsResultError = undefined;
  },
  [LEARNING_CONTRACTS.ERROR]: (state, action: PayloadAction<string>) => {
    state.learningContractsResultLoading = false;
    state.learningContractsResultRefreshing = false;
    state.learningContractsResultError = action.payload;
  },
  [LEARNING_CONTRACTS.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.learningContractsResultLoading = false;
    state.learningContractsResultRefreshing = false;
    state.learningContractsResultError = undefined;
    state.learningContractsResult = action.payload;
  },

  [INTERNSHIP_EXPERIENCE.LOAD]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.internshipExperienceSearchParams = action.payload.searchParams;
    state.internshipExperienceResultLoading = true;
    state.internshipExperienceResultRefreshing = false;
    state.internshipExperienceResultError = undefined;
  },
  [INTERNSHIP_EXPERIENCE.REFRESH]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.internshipExperienceSearchParams = action.payload.searchParams;
    state.internshipExperienceResultLoading = false;
    state.internshipExperienceResultRefreshing = true;
    state.internshipExperienceResultError = undefined;
  },
  [INTERNSHIP_EXPERIENCE.ERROR]: (state, action: PayloadAction<string>) => {
    state.internshipExperienceResultLoading = false;
    state.internshipExperienceResultRefreshing = false;
    state.internshipExperienceResultError = action.payload;
  },
  [INTERNSHIP_EXPERIENCE.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.internshipExperienceResultLoading = false;
    state.internshipExperienceResultRefreshing = false;
    state.internshipExperienceResultError = undefined;
    state.internshipExperienceResult = action.payload;
  },

  [EVALUATION_MEETINGS.LOAD]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.evaluationMeetingsSearchParams = action.payload.searchParams;
    state.evaluationMeetingsResultLoading = true;
    state.evaluationMeetingsResultRefreshing = false;
    state.evaluationMeetingsResultError = undefined;
  },
  [EVALUATION_MEETINGS.REFRESH]: (state, action: PayloadAction<IInternshipHistoryRequest>) => {
    state.evaluationMeetingsSearchParams = action.payload.searchParams;
    state.evaluationMeetingsResultLoading = false;
    state.evaluationMeetingsResultRefreshing = true;
    state.evaluationMeetingsResultError = undefined;
  },
  [EVALUATION_MEETINGS.ERROR]: (state, action: PayloadAction<string>) => {
    state.evaluationMeetingsResultLoading = false;
    state.evaluationMeetingsResultRefreshing = false;
    state.evaluationMeetingsResultError = action.payload;
  },
  [EVALUATION_MEETINGS.LOADED]: (state, action: PayloadAction<CompanyInternshipSeasonDataModel[]>) => {
    state.evaluationMeetingsResultLoading = false;
    state.evaluationMeetingsResultRefreshing = false;
    state.evaluationMeetingsResultError = undefined;
    state.evaluationMeetingsResult = action.payload;
  },
});

export default companyInsightsReducer;
